import { ChangeDetectionStrategy, Component, OnInit, computed, input, output, signal, inject } from '@angular/core';
import { injectMutation, injectQuery } from '@tanstack/angular-query-experimental';
import { BucketService } from '../../services/bucket.service';
import { MonthService } from '../../services/month.service';
import { DateTime } from 'luxon';
import { Bucket } from '../../types';
import { AnalyticsService } from '../../services/analytics.service';
import { EVENT_TRANSFERRED_BUDGET_IN } from '../../analytics/events';
import { NgForm, ReactiveFormsModule, FormsModule } from '@angular/forms';
import { TransferDirection } from './transfer-budget.types';
import { UiRadioGroupComponent } from '../../ui/radio-group/radio-group.component';
import { SelectBucketTypeaheadComponent } from '../../shared/select-bucket-typeahead/select-bucket-typeahead.component';
import { UiFieldComponent } from '../../ui/field/field.component';
import { UiCurrencyInputComponent } from '../../ui/currency-input/currency-input.component';
import { UiButtonDirective } from '../../ui/button/button.directive';
import { SuccessResponse } from '../../generated-api/models';

type TransferRequestState = { status: 'idle' } | { status: 'submitting' };

@Component({
  selector: 'app-transfer-budget',
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './transfer-budget.component.html',
  imports: [
    ReactiveFormsModule,
    FormsModule,
    UiRadioGroupComponent,
    SelectBucketTypeaheadComponent,
    UiFieldComponent,
    UiCurrencyInputComponent,
    UiButtonDirective,
  ],
})
export class TransferBudgetComponent implements OnInit {
  private analytics = inject(AnalyticsService);
  private bucketService = inject(BucketService);
  private month = inject(MonthService);

  readonly bucket = input<Bucket>();
  readonly options = input<{
    initialAmount?: number;
    hideTransferIn?: boolean;
    hideTransferOut?: boolean;
    allowNegativeAmount?: boolean;
  }>({});
  /** Drop the built-in padding when a container, such as a drawer section card, pads it. */
  readonly padded = input(true);
  readonly transferSuccess = output<SuccessResponse>();

  private readonly requestState = signal<TransferRequestState>({ status: 'idle' });
  readonly transferBudgetMutation = injectMutation(() => this.bucketService.transferBudgetMutationOptions());
  transfer: { bucketId?: string; amount?: number } = {};
  selectedBucket?: Bucket;
  direction: TransferDirection = 'in';
  readonly directionOptions = computed(() => {
    const options: { value: TransferDirection; label: string }[] = [];
    const transferOptions = this.options();
    if (!transferOptions.hideTransferIn) options.push({ value: 'in', label: 'From' });
    if (!transferOptions.hideTransferOut) options.push({ value: 'out', label: 'To' });
    return options;
  });
  /** Set when the budget is shown for a past month: the transfer is recorded there, not today. */
  readonly pastMonth = computed(() => {
    const monthYear = this.bucket()?.monthYear;
    return monthYear && monthYear < this.month.monthYearFromDate() ? monthYear : null;
  });
  readonly pastMonthLabel = computed(() => {
    const monthYear = this.pastMonth();
    return monthYear ? DateTime.fromISO(monthYear).toFormat('LLLL yyyy') : null;
  });
  // Balances and the budget list come from the month the transfer is recorded in.
  readonly bucketsQuery = injectQuery(() =>
    this.bucketService.currentMonthBucketsQueryOptions(this.pastMonth() ?? undefined),
  );

  // A past month still lists budgets archived since then, but the server rejects
  // transfers that touch an archived budget.
  readonly availableBuckets = computed(() => {
    const buckets = this.bucketsQuery.data() ?? [];
    const activeBucket = this.bucket();
    return buckets.filter(bucket => !bucket.archivedMonth && (activeBucket ? bucket.id !== activeBucket.id : true));
  });

  ngOnInit(): void {
    this.transfer.amount = this.options().initialAmount;
    if (this.options().hideTransferIn) this.direction = 'out';
    else if (this.options().hideTransferOut) this.direction = 'in';
  }

  get loading(): boolean {
    return this.requestState().status === 'submitting' || this.transferBudgetMutation.isPending();
  }

  get maxTransfer(): number | null {
    if (this.direction === 'out') return this.bucket()?.fund?.balance ?? null;
    return this.selectedBucket?.fund?.balance ?? null;
  }

  /**
   * Smallest amount allowed. With negatives enabled a negative amount reverses
   * the transfer; cap the magnitude at the balance of the bucket the money would
   * then leave, so a reversal can't pull more than that bucket holds.
   */
  get minTransfer(): number {
    if (!this.options().allowNegativeAmount) return 0.01;
    const reverseSourceBalance =
      this.direction === 'out' ? this.selectedBucket?.fund?.balance : this.bucket()?.fund?.balance;
    if (reverseSourceBalance == null) return -Number.MAX_SAFE_INTEGER;
    // A non-positive source has nothing to pull back, so keep the positive-only
    // floor — never derive a positive minimum from a negative balance.
    return reverseSourceBalance > 0 ? -reverseSourceBalance : 0.01;
  }

  private getTransferAmount(form: NgForm): number | null {
    const amount = form.controls['amount']?.value;
    if (amount === null || amount === undefined || amount === '') return null;
    return Number(amount);
  }

  transferBudget(direction: TransferDirection, form: NgForm): void {
    const amount = this.getTransferAmount(form);
    if (!this.selectedBucket || amount === null || amount === 0) return;

    this.requestState.set({ status: 'submitting' });
    const budgetMonth = this.pastMonth();

    this.transferBudgetMutation.mutate(
      {
        activeBucketId: this.bucket()?.id,
        amount,
        direction,
        monthYear: this.bucket()?.monthYear,
        ...(budgetMonth ? { budgetMonth } : {}),
        transferBucketId: this.selectedBucket.id,
      },
      {
        onSuccess: response => {
          this.selectedBucket = undefined;
          form.resetForm();
          this.transferSuccess.emit(response);
          this.analytics.sendEvent(EVENT_TRANSFERRED_BUDGET_IN);
        },
        onSettled: () => {
          this.requestState.set({ status: 'idle' });
        },
      },
    );
  }
}
